import { css } from '@emotion/css'
import type { ComboboxItem } from '@mantine/core'
import { Loader, Select as MantineSelect } from '@mantine/core'
import type { CSSProperties, FC } from 'react'
import React, { useMemo } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { colors } from 'settings/colors'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { Error } from './Icons/Error'
import { Success } from './Icons/Success'

export type TStateWithTimeout = 'default' | 'loading' | 'success' | 'error'

type SelectProps = {
  data: ComboboxItem[]
  inputStyle?: CSSProperties
  rightSection?: React.ReactNode
  clearable?: boolean
  label?: string
  labelStyle?: CSSProperties
  value?: ComboboxItem['value'] | null
  handleOnChange?: (newValue: ComboboxItem['value'] | null) => void
  stateWithTimeout?: TStateWithTimeout
  placeholder?: string
  width?: number
  disabled?: boolean
}

export const Select: FC<SelectProps> = ({
  data,
  inputStyle,
  rightSection,
  clearable = false,
  label,
  labelStyle,
  value,
  handleOnChange,
  stateWithTimeout = 'default',
  placeholder = '',
  width,
  disabled = false,
}) => {
  const isWideScreen = useIsWideScreen()

  const icon = useMemo(() => {
    switch (stateWithTimeout) {
      case 'default':
        return null

      case 'loading':
        return <Loader size="xs" />

      case 'success':
        return <Success size={18} />

      case 'error':
        return <Error size={18} />

      default:
        return null
    }
  }, [stateWithTimeout])

  return (
    <>
      <div
        className={css`
          display: flex;
          justify-content: space-between;
        `}
      >
        {label && (
          <div
            className={css`
              font-weight: 700;
              display: flex;
              align-items: center;
              height: 35px;
            `}
            style={labelStyle}
          >
            {label}
          </div>
        )}

        {!isWideScreen && icon && (
          <div
            className={css`
              width: 35px;
              height: 35px;
              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            {icon}
          </div>
        )}
      </div>
      <div
        className={css`
          flex-direction: row;
          display: flex;
        `}
      >
        <div
          css={{
            width: width ? width : '100%',
          }}
        >
          <MantineSelect
            placeholder={placeholder}
            value={value}
            onChange={(newValue) => {
              if (handleOnChange) handleOnChange(newValue)
            }}
            clearable={clearable}
            rightSection={rightSection}
            data={data}
            disabled={disabled}
            styles={{
              input: {
                borderColor: stateWithTimeout === 'error' ? colors.red : colors.grey200,
                ...inputStyle,
              },
              section:
                !clearable || !value
                  ? {
                      ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER.section,
                    }
                  : {},
            }}
          />
        </div>
        {isWideScreen && icon && (
          <div
            className={css`
              width: 35px;
              height: 35px;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-left: 5px;
            `}
          >
            {icon}
          </div>
        )}
      </div>
    </>
  )
}
